import React from 'react';
import { View, Text, TouchableOpacity, StyleSheet, TextInput, Platform } from 'react-native';
import Svg, { Defs, Path, RadialGradient, Stop, Ellipse, G } from 'react-native-svg';
import { useLanguage } from '../contexts/LanguageContext';

interface BillPlusBarProps {
  onUpgradePress: () => void;
  inputValue: string;
  onChangeText: (text: string) => void;
  onSendPress: () => void;
  onResetPress: () => void;
  inputPlaceholder: string;
  sendDisabled: boolean;
  remainingMessages?: number;
}

const CardBackground = () => (
  <View style={styles.cardBackgroundContainer}>
    <Svg width="100%" height="100%" viewBox="0 0 469 207" preserveAspectRatio="xMidYMid slice">
      <Defs>
        <RadialGradient id="glow1" cx="50%" cy="50%" rx="50%" ry="50%">
          <Stop offset="0%" stopColor="white" stopOpacity="0.9" />
          <Stop offset="40%" stopColor="white" stopOpacity="0.4" />
          <Stop offset="100%" stopColor="white" stopOpacity="0" />
        </RadialGradient>
        <RadialGradient id="glow2" cx="50%" cy="50%" rx="50%" ry="50%">
          <Stop offset="0%" stopColor="#015480" stopOpacity="1" />
          <Stop offset="50%" stopColor="#015480" stopOpacity="0.5" />
          <Stop offset="100%" stopColor="#015480" stopOpacity="0" />
        </RadialGradient>
        <RadialGradient id="glow3" cx="50%" cy="50%" rx="50%" ry="50%">
          <Stop offset="0%" stopColor="#015480" stopOpacity="0.9" />
          <Stop offset="50%" stopColor="#015480" stopOpacity="0.4" />
          <Stop offset="100%" stopColor="#015480" stopOpacity="0" />
        </RadialGradient>
      </Defs>
      <G opacity={0.9}>
        <Ellipse cx="60" cy="120" rx="140" ry="130" fill="url(#glow1)" />
      </G>
      <Ellipse cx="300" cy="140" rx="150" ry="140" fill="url(#glow2)" />
      <Ellipse cx="420" cy="40" rx="100" ry="100" fill="url(#glow3)" />
    </Svg>
  </View>
);

export function BillPlusBar({ onUpgradePress, inputValue, onChangeText, onSendPress, onResetPress, inputPlaceholder, sendDisabled, remainingMessages = 0 }: BillPlusBarProps) {
  const { t } = useLanguage();
  
  const getPromptText = () => {
    if (remainingMessages && remainingMessages > 0) {
      return `${t('bill.plusBar.remainingMessages')}${remainingMessages}${t('bill.plusBar.remainingMessagesEnd')}`;
    }
    return t('bill.plusBar.continueConversation');
  };

  return (
    <View style={styles.wrapper}>
      <View style={styles.blueCard}>
        <CardBackground />

        <TouchableOpacity 
          style={styles.upgradePromptRow}
          onPress={onUpgradePress}
          activeOpacity={0.8}
        >
          <Svg width="20" height="20" viewBox="0 0 20 20" fill="none">
            <Path 
              d="M2.92539 9.60617L9.01358 16.2952C9.54225 16.8765 10.4568 16.8765 10.9854 16.2952L17.0745 9.60533C17.5837 9.04675 17.642 8.21163 17.2163 7.58651L14.7645 3.98498C14.4605 3.53904 13.9546 3.27148 13.4146 3.27148H6.59259C6.0526 3.27148 5.54748 3.53823 5.24343 3.98417L2.78431 7.58651C2.35783 8.21082 2.41622 9.04675 2.92539 9.60617Z" 
              stroke="white" 
              strokeWidth="1.5" 
              strokeLinecap="round" 
              strokeLinejoin="round" 
            />
            <Path 
              d="M11.6667 3.27148L12.8059 7.58651C12.9712 8.21163 12.9486 9.04675 12.7507 9.60533L10.3841 16.2952C10.1789 16.8765 9.82298 16.8765 9.61781 16.2952L7.25114 9.60617C7.05331 9.04675 7.03061 8.21082 7.19682 7.58651L8.33923 3.27148" 
              stroke="white" 
              strokeWidth="1.5" 
              strokeLinecap="round" 
              strokeLinejoin="round" 
            />
            <Path 
              d="M2.50586 8.50244H17.4917" 
              stroke="white" 
              strokeWidth="1.5" 
              strokeLinecap="round" 
              strokeLinejoin="round" 
            />
          </Svg>

          <Text style={styles.upgradePromptText}>{getPromptText()}</Text>

          <Svg width="16" height="16" viewBox="0 0 16 16" fill="none">
            <Path 
              d="M5.83398 3.3335L10.5007 8.00016L5.83398 12.6668" 
              stroke="white" 
              strokeWidth="2" 
              strokeLinecap="square" 
            />
          </Svg>
        </TouchableOpacity>

        <View style={styles.inputContainer}>
          <View style={styles.textInputFrame}>
            <TextInput
              style={styles.textInput}
              value={inputValue}
              onChangeText={onChangeText}
              placeholder={inputPlaceholder}
              placeholderTextColor="#A19F97"
              multiline
              maxLength={500}
            />
          </View>

          <View style={styles.buttonsRow}>
            <TouchableOpacity style={styles.refreshButton} onPress={onResetPress}>
              <Svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <Path 
                  d="M11.2817 10.683C10.4309 11.916 9.00395 12.7238 7.38848 12.7238C4.78225 12.7238 2.66602 10.613 2.66602 8.0013C2.66602 5.39504 4.78225 3.27881 7.38848 3.27881C9.95708 3.27881 12.0463 5.32504 12.1163 7.87743" 
                  stroke="#7E7D75" 
                  strokeWidth="1.5" 
                  strokeLinecap="round" 
                  strokeLinejoin="round" 
                />
                <Path 
                  d="M13.334 5.39844L12.2619 7.93413L9.74023 6.86793" 
                  stroke="#7E7D75" 
                  strokeWidth="1.5" 
                  strokeLinecap="round" 
                  strokeLinejoin="round" 
                />
              </Svg>
            </TouchableOpacity>

            <TouchableOpacity 
              style={[styles.sendButton, sendDisabled && styles.sendButtonDisabled]} 
              onPress={onSendPress}
              disabled={sendDisabled}
            >
              <Svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <Path 
                  d="M8.16602 3.3999V13" 
                  stroke="white" 
                  strokeWidth="1.5" 
                  strokeLinecap="square" 
                />
                <Path 
                  d="M12.183 7.03331L8.16704 3L4.15039 7.03331" 
                  stroke="white" 
                  strokeWidth="1.5" 
                  strokeLinecap="square" 
                />
              </Svg>
            </TouchableOpacity>
          </View>
        </View>
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  wrapper: {
    width: '100%',
    maxWidth: 369,
    alignSelf: 'center',
  },
  blueCard: {
    borderRadius: 24,
    backgroundColor: '#669AB7',
    width: '100%',
    flexDirection: 'column',
    justifyContent: 'flex-start',
    alignItems: 'center',
    overflow: 'hidden',
    position: 'relative',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.05,
    shadowRadius: 14,
    elevation: 4,
  },
  cardBackgroundContainer: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
  },
  upgradePromptRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    width: '100%',
    zIndex: 1,
    paddingTop: Platform.OS === 'android' ? 14 : 12,
    paddingBottom: 12,
    paddingHorizontal: 12,
  },
  upgradePromptText: {
    color: '#FFFFFF',
    fontFamily: 'Inter_700Bold',
    fontSize: 14,
    lineHeight: 19.6,
    flex: 1,
  },
  inputContainer: {
    borderRadius: 24,
    borderWidth: 1,
    borderColor: '#F0EEE4',
    backgroundColor: '#FFFFFF',
    paddingTop: Platform.OS === 'android' ? 14 : 16,
    paddingBottom: 12,
    paddingHorizontal: 12,
    width: '100%',
    zIndex: 1,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.05,
    shadowRadius: 14,
    elevation: 4,
  },
  textInputFrame: {
    paddingHorizontal: 4,
    marginBottom: 12,
  },
  textInput: {
    fontFamily: 'Inter_500Medium',
    fontSize: 14,
    lineHeight: 19.6,
    minHeight: 20,
    color: '#151515',
    padding: 0,
  },
  buttonsRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    width: '100%',
  },
  refreshButton: {
    width: 32,
    height: 32,
    borderRadius: 27,
    backgroundColor: '#F0EEE4',
    justifyContent: 'center',
    alignItems: 'center',
  },
  sendButton: {
    width: 32,
    height: 32,
    borderRadius: 27,
    backgroundColor: '#151515',
    justifyContent: 'center',
    alignItems: 'center',
  },
  sendButtonDisabled: {
    opacity: 0.5,
  },
});
